import React, { useEffect, useState } from "react";
import { apiClient } from "@/api/index";
import { User } from "@/api/types";
import SimplePage from "@/components/structure/SimplePage";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import { faStar, faUser } from "@fortawesome/free-solid-svg-icons";
import { useParams } from "react-router";
import { useAuth } from "@/contexts/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// setting to show the cohort list in the profile, in case we want to enable it later
const ALLOW_USER_TO_EDIT_COHORTS = false;

export default function Profile() {
  const { isAuthenticated, user: authUser } = useAuth();
  const { userId } = useParams<{ userId?: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [cohorts, setCohorts] = useState<string[]>([]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        if (userId) {
          const response = await apiClient.get<User>(`/users/${userId}/`);
          setUser(response);
        } else {
          const response = await apiClient.get<User>("/current-user/");
          setUser(response);
        }
      } catch (err) {
        setError("Failed to fetch user data");
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [userId]);

  useEffect(() => {
    const fetchCohorts = async () => {
      try {
        const response = await apiClient.get<{ cohorts: { id: string; name: string }[] }>("/cohorts/");
        setCohorts(response);
      } catch (err) {
        console.error("Failed to fetch cohorts", err);
      }
    }

    if (ALLOW_USER_TO_EDIT_COHORTS) {
      fetchCohorts();
    }
  }, []);

  const updateCohorts = (newCohortIds: string[]) => {
    // make a PATCH request to update the user's cohorts on the backend, then update the local state
    const updateUserCohorts = async () => {
      try {
        const response = await apiClient.patch(`/users/${user?.id}/`, { cohort_ids: newCohortIds });
        // use response.cohorts to update the user's cohorts in local state
        setUser(prev => prev ? { ...prev, cohorts: response.cohorts } : prev);
      } catch (err) {
        console.error("Failed to update user cohorts", err);
      }
    }
    if (ALLOW_USER_TO_EDIT_COHORTS) {
      updateUserCohorts();
    }
  }

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!user) return <div>No user data found</div>;

  const fields = [
    { label: "Pseudonym", value: user.pseudonym },
    {
      label: "Name",
      value:
        user.firstName && user.lastName && `${user.firstName} ${user.lastName}`,
    },
    { label: "Username", value: user.username },
    { label: "Email Address", value: user.email },
    { label: "Project Admin?", value: user.isProjectsAdmin ? "Yes" : "No" },
    {
      label: "Date Joined",
      value: user.dateJoined && new Date(user.dateJoined).toLocaleString(),
    },
  ];

  const preferredName = user.firstName
    ? user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.firstName
    : user.pseudonym;

  const titleElem = (
    <div className="flex items-center space-x-2">
      <FontAwesomeIcon icon={faUser} className="text-gray-600 text-2xl" />
      <span>Profile for {preferredName}</span>
      {isAuthenticated && authUser?.id === user.id && (
        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
          <FontAwesomeIcon icon={faStar} /> You
        </span>
      )}
    </div>
  );

  return (
    <ProtectedRoute>
      <SimplePage title={titleElem}>
        <div className="space-y-4 ml-2 mt-8 container border border-gray-300 rounded-lg p-4 bg-white shadow-md">
          <div className="flex space-x-4">
            {user.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt="User Avatar"
                className="w-16 h-16 rounded-full"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-gray-300 flex items-center justify-center text-gray-600">
                No Avatar
              </div>
            )}

            <div>
              {fields
                .filter((entry) => entry.value)
                .map((entry) => (
                  <div key={entry.label} className="mb-4">
                    <label className="block text-lg font-bold text-gray-700">
                      {entry.label}
                    </label>
                    <div className="text-lg text-gray-900">{entry.value}</div>
                  </div>
              ))}

              {/* show all cohorts as a checkbox list, then check the ones in which the user is included */ }
              {
                ALLOW_USER_TO_EDIT_COHORTS && (
                  <div className="space-y-2">
                    <label className="block text-lg font-bold text-gray-700">Cohorts</label>

                    {cohorts.map((cohort) => (
                      <div key={cohort.id} className="flex items-center">
                        <input
                          id={`cohort-${cohort.id}`}
                          type="checkbox"
                          checked={user.cohorts?.some((c) => c.id === cohort.id) || false}
                          onChange={(e) => {
                            const newCohortIds = e.target.checked
                              ? [...(user.cohorts || []).map(x => x.id), cohort.id]
                              : (user.cohorts?.filter((c) => c.id !== cohort.id) || []).map(x => x.id);
                            updateCohorts(newCohortIds);
                          }}
                          className="mr-2"
                        />
                        <label htmlFor={`cohort-${cohort.id}`}>{cohort.name}</label>
                      </div>
                    ))}
                  </div>
                )
              }
            </div>
          </div>
        </div>
      </SimplePage>
    </ProtectedRoute>
  );
}
